// AGC: Button - Reusable button component for Classic Navy Theme
// AGC: Pill-shaped navy buttons with white text
import React from 'react'
import { Loader2 } from 'lucide-react'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline'
type ButtonSize = 'sm' | 'md' | 'lg'
type FeedbackStrength = 'strong' | 'subtle'

interface ButtonProps {
  children: React.ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  disabled?: boolean
  loading?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  className?: string
  /** AGC: strong = ring glow on primary/danger; subtle = scale/opacity only. Default: strong when size=lg */
  feedback?: FeedbackStrength
}

/**
 * AGC: Button - Multi-variant button component for Classic Navy Theme
 * AGC: Primary: Navy solid pill with white text
 * AGC: Secondary: Navy-50 pill with navy text
 * AGC: Ghost: Transparent with navy text, subtle hover
 * AGC: Outline: Navy border pill with navy text
 * AGC: Danger: Red solid pill with white text
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'left',
  onClick,
  type = 'button',
  className = '',
  feedback,
}) => {
  // AGC: Inference: strong when size=lg or explicit, else subtle
  const resolvedFeedback: FeedbackStrength = feedback ?? (size === 'lg' ? 'strong' : 'subtle')

  // AGC: Variant styles using CSS variables for dynamic theming
  // AGC: Primary/danger get ring glow when feedback=strong
  const variantClasses: Record<ButtonVariant, string> = {
    // AGC: Primary - Accent solid pill button (uses CSS variable)
    primary: resolvedFeedback === 'strong'
      ? `
        text-white
        hover:opacity-90 hover:shadow-md
        active:opacity-80 active:ring-4 active:ring-[var(--ring-click-primary)] active:shadow-lg
        shadow-sm
      `
      : `
        text-white
        hover:opacity-90
        active:opacity-80
        shadow-sm
      `,
    // AGC: Secondary - Light accent pill with accent text
    secondary: `
      bg-gray-100 text-gray-700
      hover:bg-gray-200
      active:bg-gray-300
      dark:bg-slate-700 dark:text-gray-200
      dark:hover:bg-slate-600
    `,
    // AGC: Ghost - Transparent with gray text
    ghost: `
      bg-transparent text-gray-600
      hover:bg-gray-100
      active:bg-gray-200
      dark:text-gray-400 dark:hover:bg-slate-700
    `,
    // AGC: Outline - Accent border pill
    outline: `
      bg-transparent text-gray-700
      border-2 border-gray-300
      hover:bg-gray-50
      hover:border-gray-400
      active:bg-gray-100
      dark:text-gray-300 dark:border-gray-600
    `,
    // AGC: Danger - Red solid pill
    danger: resolvedFeedback === 'strong'
      ? `
        bg-red-500 text-white
        hover:bg-red-600 hover:shadow-md
        active:bg-red-700 active:ring-4 active:ring-[var(--ring-click-danger)] active:shadow-lg
        focus-visible:ring-red-500/50
        shadow-sm
      `
      : `
        bg-red-500 text-white
        hover:bg-red-600
        active:bg-red-700
        focus-visible:ring-red-500/50
        shadow-sm
      `,
  }

  // AGC: Size styles with pill shape
  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'px-4 py-1.5 text-sm gap-1.5',
    md: 'px-5 py-2.5 text-sm gap-2',
    lg: 'px-6 py-3 text-base gap-2',
  }

  // AGC: Icon size based on button size
  const iconSizeClasses: Record<ButtonSize, string> = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  }

  // AGC: Base classes with pill shape (rounded-full)
  const baseClasses = `
    inline-flex items-center justify-center
    rounded-full
    font-medium
    transition-all duration-200
    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
    active:scale-[0.97]
    disabled:active:scale-100
  `

  // AGC: Width classes
  const widthClasses = fullWidth ? 'w-full' : ''

  // AGC: Clean up whitespace from template literals
  const cleanClass = (cls: string) => cls.replace(/\s+/g, ' ').trim()

  // AGC: Render icon with proper sizing
  const renderIcon = (position: 'left' | 'right') => {
    if (loading && position === 'left') {
      return <Loader2 className={`${iconSizeClasses[size]} animate-spin`} />
    }
    if (icon && iconPosition === position) {
      return <span className={iconSizeClasses[size]}>{icon}</span>
    }
    return null
  }

  // AGC: Get dynamic background style for primary variant
  const primaryStyle = variant === 'primary' ? { backgroundColor: 'var(--accent-primary)' } : undefined

  return (
    <button
      type={type}
      className={cleanClass(`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${widthClasses}
        ${className}
      `)}
      style={primaryStyle}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {renderIcon('left')}
      {loading ? 'Loading...' : children}
      {renderIcon('right')}
    </button>
  )
}

/**
 * AGC: IconButton - Button with only an icon (no text) for Classic Navy Theme
 */
interface IconButtonProps {
  icon: React.ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  disabled?: boolean
  loading?: boolean
  onClick?: () => void
  className?: string
  'aria-label': string
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  variant = 'ghost',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  className = '',
  'aria-label': ariaLabel,
}) => {
  // AGC: Icon button size (square with rounded corners)
  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  }

  const iconSizeClasses: Record<ButtonSize, string> = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  }

  // AGC: Variant styles - IconButton primary always gets ring glow (CTAs)
  const variantClasses: Record<ButtonVariant, string> = {
    primary: 'text-white hover:opacity-90 active:opacity-80 active:ring-4 active:ring-[var(--ring-click-primary)] shadow-sm',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-200',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-700',
    outline: 'bg-transparent text-gray-700 border-2 border-gray-300 hover:bg-gray-50 dark:text-gray-300 dark:border-gray-600',
    danger: 'bg-red-500 text-white hover:bg-red-600 shadow-sm',
  }

  // AGC: Get dynamic background style for primary variant
  const primaryStyle = variant === 'primary' ? { backgroundColor: 'var(--accent-primary)' } : undefined

  return (
    <button
      type="button"
      className={`
        inline-flex items-center justify-center
        rounded-lg
        transition-all duration-200
        focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed
        active:scale-[0.95]
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${className}
      `.replace(/\s+/g, ' ').trim()}
      style={primaryStyle}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
    >
      {loading ? (
        <Loader2 className={`${iconSizeClasses[size]} animate-spin`} />
      ) : (
        <span className={iconSizeClasses[size]}>{icon}</span>
      )}
    </button>
  )
}

export default Button
